import { mkSiteBuilderComponent } from "@discovery/template-engine/lib/component-map";
import { useGlobalState } from "@discovery/common-dplus/lib/hooks";
import { _Config } from "@discovery/common-dplus/lib/state";
import { Fold, Lens } from "@discovery/prelude/lib/control/lens";
import * as M from "@discovery/prelude/lib/data/maybe";
import { optional, record, string, } from "@discovery/prelude/lib/data/validated";
import { _Col2ComponentId, _Col2Id, _Col2TemplateId, _ColAttributes2Title, _GetCustomAttributesForComponent, } from "@discovery/sonic-api-ng-optics";
import { BannerAdPlaceholder, BannerIdM, _RailData, _MastHeadCarousel, _CarouselSquareLogo, _PosterRailData, } from "./optics";
import { chName, ShowNameM, GenreM } from "../banner-ads/optics";
import { useAds } from "../../utils/hooks";
import { _ShowAndVideoCollection } from "../optics";
const View = React.lazy(() => import("./view"));
export var RailTypes;
(function (RailTypes) {
    RailTypes[RailTypes["Basic"] = 0] = "Basic";
    RailTypes[RailTypes["SuperStars"] = 1] = "SuperStars";
    RailTypes[RailTypes["Descriptive"] = 2] = "Descriptive";
    RailTypes[RailTypes["PremiumCarousel"] = 3] = "PremiumCarousel";
    RailTypes[RailTypes["CategoryCard"] = 4] = "CategoryCard";
    RailTypes[RailTypes["FeaturedLarge"] = 5] = "FeaturedLarge";
    RailTypes[RailTypes["Search"] = 6] = "Search";
    RailTypes[RailTypes["BasicWithPosition"] = 7] = "BasicWithPosition";
    RailTypes[RailTypes["Poster"] = 8] = "Poster";
    RailTypes[RailTypes["SquarLogo"] = 9] = "SquarLogo";
    RailTypes[RailTypes["DescriptiveWithProgress"] = 10] = "DescriptiveWithProgress";
    RailTypes[RailTypes["FeaturedStandalone"] = 11] = "FeaturedStandalone";
})(RailTypes || (RailTypes = {}));
export const templateIdToRailType = (templateId) => {
    switch (templateId) {
        case "basic":
            return RailTypes.Basic;
        case "basic-with-position":
            return RailTypes.BasicWithPosition;
        case "basic-superstars":
            return RailTypes.SuperStars;
        case "descriptive-with-progress":
            return RailTypes.DescriptiveWithProgress;
        case "descriptive":
            return RailTypes.Descriptive;
        case "square-title-compact":
            return RailTypes.Poster;
        case "square-logo":
            return RailTypes.SquarLogo;
        case "pwa-rectangle":
            return RailTypes.CategoryCard;
        case "single-item":
            return RailTypes.PremiumCarousel;
        case "featured-standalone":
            return RailTypes.FeaturedStandalone;
        default:
            return RailTypes.Basic;
    }
};
const getProps = (data) => {
    const bannerAdPlaceholder = Fold.preview(BannerAdPlaceholder, data);
    const bannerIdM = Fold.preview(BannerIdM, data);
    const BannerId = M.fromMaybe(bannerIdM, "");
    const backupAds = M.fromMaybe(bannerAdPlaceholder, {});
    const channelNameM = Fold.preview(chName, data);
    const showNameM = Fold.preview(ShowNameM, data);
    const genreM = Fold.preview(GenreM, data);
    const channelName = M.fromMaybe(channelNameM, "");
    const showName = M.fromMaybe(showNameM, "");
    const genre = M.fromMaybe(genreM, "");
    const splitString = (value) => value.split(":");
    const channel = splitString(channelName);
    const show = splitString(showName);
    const genreName = splitString(genre);
    const targetingValues = Fold.toArray(_ShowAndVideoCollection, data);
    const primaryChannel = targetingValues[0] && M.fromMaybe(targetingValues[0].primaryChannel, "");
    const channelTitle = channel.length > 1 && channel[1] && channel[1].includes("$%")
        ? primaryChannel
        : channel[1];
    const showTitle = show.length > 1 && show[1] && show[1].includes("$%")
        ? targetingValues[0] && targetingValues[0].name
        : show[1];
    const genreCategory = genreName.length > 1 && genreName[1] && genreName[1].includes("$%")
        ? M.fromMaybe(targetingValues[0].genre, "")
        : genreName[1];
    const contentGridTitleM = Fold.preview(Fold.compose(_ColAttributes2Title, M._Just()), data);
    const templateIdM = Fold.preview(_Col2TemplateId, data);
    const componentIdM = Fold.preview(_Col2ComponentId, data);
    const componentId = M.fromMaybe(componentIdM, "");
    const templateId = M.fromMaybe(templateIdM, "");
    const mapComponentIdToTeamplateId = () => {
        switch (componentId) {
            case "carousel-superstar":
                return "basic-superstars";
            case "featured-standalone":
                return "featured-standalone";
            default:
                return templateId;
        }
    };
    const railType = templateIdToRailType(mapComponentIdToTeamplateId());
    const title = M.fromMaybe(contentGridTitleM, "");
    const contentGridIdM = Fold.preview(_Col2Id, data);
    const gridID = M.fromMaybe(contentGridIdM, "");
    const customAttrValidator = optional(record({
        viewAllRoute: string,
    }));
    const viewAllRouteM = Fold.preview(Fold.compose(Fold.pre(_GetCustomAttributesForComponent(customAttrValidator)), M._Just(), M._Just(), Lens.prop("viewAllRoute")), data);
    const getCardArray = () => {
        switch (railType) {
            case RailTypes.SquarLogo:
                return Fold.toArray(_CarouselSquareLogo, data);
            case RailTypes.CategoryCard:
                return Fold.toArray(_MastHeadCarousel, data);
            case RailTypes.Poster:
                return Fold.toArray(_PosterRailData, data);
            default:
                return Fold.toArray(_RailData, data);
        }
    };
    const cardArray = getCardArray();
    return {
        cardArray,
        title,
        railType,
        viewAllRouteM,
        gridID,
        BannerId,
        backupAds,
        channelTitle,
        showTitle,
        genreCategory,
        componentId,
    };
};
const Rails = ({ cardArray, title, railType, viewAllRouteM, gridID, BannerId, backupAds, channelTitle, showTitle, genreCategory, environment, }) => {
    const [configM] = useGlobalState(_Config);
    const { adsHasValidPackage, enableAds } = useAds();
    const webAuthUrl = environment.authService.protocol + "://" + environment.authService.domain;
    //Config
    const parentalLockM = M.chain((x) => x.parentalLock, configM);
    const minimumAgeM = M.chain((x) => x.minimumAge, parentalLockM);
    const ageConstraint = M.fromMaybe(minimumAgeM, 0);
    const isAdsEnabled = enableAds && !adsHasValidPackage;
    //KIDS
    return (React.createElement(React.Suspense, { fallback: () => React.createElement(React.Fragment, null) },
        React.createElement(View, { cardArray: cardArray, title: title, railType: railType, read: environment, webAuthRedirectionUrl: webAuthUrl, gridID: gridID, viewAllRouteM: viewAllRouteM, ageConstraint: ageConstraint, BannerId: BannerId, bannerAdPlaceholder: backupAds, isAdsEnabled: isAdsEnabled, channelTitle: channelTitle, showTitle: showTitle, genreCategory: genreCategory })));
};
export const ComponentMapItem = mkSiteBuilderComponent(getProps, Rails);

